import {
  DEFAULT_LOCALE,
  ENABLED_LOCALES,
  isEnabledLocale,
  type Locale,
} from './config'

/** Long enough for any real browser; a hostile header is read no further. */
const MAX_HEADER_CHARS = 512
const MAX_ENTRIES = 24

type Preference = { tag: string; weight: number; order: number }

function parseAcceptLanguage(header: string): Array<Preference> {
  const preferences: Array<Preference> = []
  const entries = header.slice(0, MAX_HEADER_CHARS).split(',')
  for (const [order, entry] of entries.slice(0, MAX_ENTRIES).entries()) {
    const [rawTag, ...params] = entry.split(';')
    const tag = rawTag?.trim().toLowerCase() ?? ''
    if (tag === '' || tag === '*') {
      continue
    }
    const quality = params
      .map((param) => param.trim())
      .find((param) => param.startsWith('q='))
    const parsed = quality ? Number.parseFloat(quality.slice(2)) : 1
    const weight = Number.isFinite(parsed) ? parsed : 1
    if (weight > 0) {
      preferences.push({ tag, weight, order })
    }
  }
  return preferences.sort((a, b) => b.weight - a.weight || a.order - b.order)
}

/**
 * The enabled locale an `Accept-Language` header asks for, else English.
 * A tag matches a locale exactly (`pt-br`) or by its language (`de-AT` →
 * `de`, `pt-PT` → `pt-br`).
 *
 * Used for the product only (one URL per page): the marketing tree never
 * reads `Accept-Language`, because a URL must name the language it serves.
 */
export function negotiateLocale(
  header: string | null | undefined,
  enabledLocales: ReadonlyArray<Locale> = ENABLED_LOCALES
): Locale {
  for (const { tag } of parseAcceptLanguage(header ?? '')) {
    if (isEnabledLocale(tag, enabledLocales)) {
      return tag
    }
    const language = tag.split('-')[0]
    const sameLanguage = enabledLocales.find(
      (locale) => locale.split('-')[0] === language
    )
    if (sameLanguage) {
      return sameLanguage
    }
  }
  return DEFAULT_LOCALE
}
